import React,{useEffect,useState} from "react";
import { DetailsBox, EmptyField, FieldShell } from "../components/FieldShell";
import LongTextValue from "./LongTextValue";
import { googleMapsEmbedSrc, googleMapsHref } from "../utils/mapUtils";
import {
  buildDetailSource,
  extractApiArray,
} from "../utils/arrayUtils";
import {
  dropdownLabelFromRow,
  formatDisplayValue,
  getBaseField,
  isTrue,
  objectLabel,
} from "../utils/valueUtils";
import { isHttpUrl } from "../utils/fileUtils";

const dropdownApiCache = new Map();

export function PrimitiveField({ label, column, raw }) {
  const [expanded, setExpanded] = useState(false);

  const value = formatDisplayValue(raw, column);
  const stringValue = String(value ?? "");

  const previewLines = Math.max(1, Number(column?.previewLines) || 3);

  /*
   * Collapse the previous value when the table moves
   * to another row or the field value changes.
   */
  useEffect(() => {
    setExpanded(false);
  }, [raw, column?.field]);

  if (stringValue.trim() === "" || stringValue === "—") {
    return (
      <FieldShell label={label} column={column}>
        <span className="safety-row-details__dash">—</span>
      </FieldShell>
    );
  }

  return (
    <FieldShell
      label={label}
      column={column}
      className={[
        "safety-row-details__field--long-text",
        expanded ? "safety-row-details__field--text-expanded" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <LongTextValue
        value={stringValue}
        lines={previewLines}
        expanded={expanded}
        onExpandedChange={setExpanded}
      />
    </FieldShell>
  );
}

export function BooleanField({ label, column, raw }) {
  return (
    <FieldShell label={label} column={column}>
      <span
        className={`safety-row-details__chip ${
          raw
            ? "safety-row-details__chip--success"
            : "safety-row-details__chip--muted"
        }`}
      >
        {raw ? "Yes" : "No"}
      </span>
    </FieldShell>
  );
}

export function DropdownField({ row, label, column }) {
  const baseField = getBaseField(column.field);
  const selectedOption = row?.[baseField];
  const fallbackLabel = dropdownLabelFromRow(row, column);
  const [resolvedOption, setResolvedOption] = useState(null);

  useEffect(() => {
    const selectedIsPopulated =
      selectedOption &&
      typeof selectedOption === "object" &&
      !Array.isArray(selectedOption) &&
      Boolean(
        (column.apiTitle && selectedOption[column.apiTitle]) ||
          selectedOption.name ||
          selectedOption.label ||
          selectedOption.title,
      );

    if (!column.api || !selectedOption || selectedIsPopulated) {
      setResolvedOption(null);
      return undefined;
    }

    const controller = new AbortController();

    const resolveSelectedOption = async () => {
      try {
        let rows = dropdownApiCache.get(column.api);

        if (!rows) {
          const token =
            localStorage.getItem("token") ||
            localStorage.getItem("accessToken") ||
            localStorage.getItem("authToken");

          const response = await fetch(column.api, {
            method: "GET",
            signal: controller.signal,
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to resolve ${label}`);
          }

          rows = extractApiArray(await response.json());
          dropdownApiCache.set(column.api, rows);
        }

        if (!controller.signal.aborted) {
          setResolvedOption(
            buildDetailSource(selectedOption, rows, column),
          );
        }
      } catch (error) {
        if (error.name !== "AbortError") setResolvedOption(null);
      }
    };

    resolveSelectedOption();
    return () => controller.abort();
  }, [
    selectedOption,
    column.api,
    column.apiTitle,
    column.field,
    label,
  ]);

  const labelText =
    (resolvedOption && objectLabel(resolvedOption, column)) || fallbackLabel;

  const detailsSource =
    resolvedOption ||
    (selectedOption && typeof selectedOption === "object"
      ? selectedOption
      : null);

  return (
    <FieldShell
      label={label}
      column={column}
      details={
        isTrue(column.details) && detailsSource ? (
          <DetailsBox source={detailsSource} fields={column.BoxShow} />
        ) : null
      }
    >
      {labelText ? (
        <span className="safety-row-details__chip">{labelText}</span>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

export function SignatureField({ label, column, raw }) {
  if (!isHttpUrl(raw)) {
    return <EmptyField label={label} column={column} />;
  }

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--images"
    >
      <div className="safety-row-details__signature-preview">
        <img
          className="safety-row-details__signature"
          src={raw}
          alt={`${label} signature`}
          onClick={() => window.open(raw, "_blank", "noopener,noreferrer")}
          onError={(event) => {
            event.currentTarget.replaceWith(
              Object.assign(document.createElement("span"), {
                className: "safety-row-details__dash",
                textContent: "—",
              }),
            );
          }}
        />

        <a
          href={raw}
          target="_blank"
          rel="noopener noreferrer"
          className="safety-row-details__link"
        >
          View Full Signature
        </a>
      </div>
    </FieldShell>
  );
}

export function GeolocationField({ label, column, raw }) {
  const embedSrc = googleMapsEmbedSrc(raw);
  const href = googleMapsHref(raw);

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--map"
    >
      {embedSrc ? (
        <>
          <div className="safety-row-details__map-embed">
            <iframe
              title="Map"
              src={embedSrc}
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {href ? (
            <a
              className="safety-row-details__link"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </a>
          ) : null}
        </>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

